/**
 * Avaliação de um operador num período (RF-D01..D06): a nota de cada dia e a
 * média do período, a partir do mesmo resumo que alimenta o "Meu dia" e o
 * aproveitamento da gestão.
 *
 *  - Por dia: eficiência e produtividade vêm de `calcularResumoPeriodo`;
 *    pontualidade compara o primeiro login no posto com o início do turno.
 *    Cada critério vira um valor em [0, 1] (bater a meta = 1) e entra na
 *    nota com o peso cadastrado — ou com o peso sobrescrito para o setor /
 *    produto em que o operador trabalhou, ponderado pelo tempo em cada um.
 *  - Novatos: eficiência e produtividade passam pela curva de experiência
 *    (`aplicarCurvaExperiencia`); pontualidade não — chegar no horário não
 *    depende de prática.
 *  - Sessão de posto esquecida aberta vence ao fim do turno do dia em que
 *    começou (`fimPrevistoDaSessao`): não vira dia trabalhado, pontualidade
 *    nem tempo logado nos dias seguintes.
 *  - Ausência registrada (feriado, atestado, férias…) tira a jornada do
 *    dia: não é falta e não conta tempo disponível; quem trabalhou mesmo
 *    assim é avaliado normalmente. O atestado pode contar como falta, se a
 *    empresa configurar (`RegrasFalta`, contrato 8.1).
 *  - Nota do período = média das notas diárias existentes (RF-D03). Dia de
 *    turno sem login nem ausência é falta. O que a falta faz é regra da
 *    empresa: por padrão fica sem nota (quem bate a meta nos outros dias
 *    recupera) e não tira a faixa; pode entrar na média com a nota mínima
 *    e/ou tirar a faixa do período a partir de N faltas.
 *
 * Tudo aqui é puro; a API só carrega os dados e entrega.
 */
import { reduzirEventosNoIntervalo } from '../apontamento/intervalo.js';
import { calcularResumoPeriodo, } from '../indicadores/resumo-periodo.js';
import { fimPrevistoDaSessao, jornadaNoIntervalo, jornadaSegundos, turnoNoDia, } from '../tempo/jornada.js';
import { janelasDoTurno } from '../tempo/fim-do-turno.js';
import { diasNoIntervalo } from '../tempo/periodo.js';
import { NOTA_MINIMA, aplicarCurvaExperiencia, classificarFaixa, calcularNota, mediaMensal, toleranciaExperiencia, } from './nota.js';
/**
 * Códigos de critério que o sistema sabe calcular. Um critério cadastrado
 * com outro código aparece na tela como "não calculado" e não entra na nota.
 */
export const CRITERIOS_CALCULADOS = ['eficiencia', 'produtividade', 'pontualidade'];
export const PERIODICIDADES_BONUS = ['SEMANAL', 'QUINZENAL', 'MENSAL'];
/** Período de consulta correspondente à periodicidade de bônus configurada. */
export function periodoDaPeriodicidade(periodicidade) {
    switch (periodicidade) {
        case 'SEMANAL':
            return 'semana';
        case 'QUINZENAL':
            return 'quinzena';
        case 'MENSAL':
            return 'mes';
    }
}
/** Tipos de ausência (cadastro `Ausencia`): feriado vale para todos; os demais, por operador. */
export const TIPOS_AUSENCIA = ['FERIADO', 'ATESTADO', 'FERIAS', 'OUTRO'];
/** O comportamento de antes da configuração: falta à parte, atestado abona, faixa mantida. */
export const REGRAS_FALTA_PADRAO = {
    atestadoContaComoFalta: false,
    faltaEntraNaNota: false,
    faltasParaPerderFaixa: null,
};
/**
 * Pontualidade do dia em [0, 1]: 1 dentro da tolerância, 0 a partir do
 * atraso máximo, linear entre os dois.
 */
export function valorPontualidade(atrasoSeg, p) {
    const excedente = Math.max(0, atrasoSeg) - p.toleranciaSeg;
    if (excedente <= 0)
        return 1;
    const faixa = p.atrasoMaximoSeg - p.toleranciaSeg;
    if (faixa <= 0)
        return 0;
    return Math.max(0, 1 - excedente / faixa);
}
function pesoPara(padrao, t, sobrescritas) {
    // A mais específica vence: setor + produto → setor → produto → padrão.
    const exata = sobrescritas.find((s) => s.setorId != null &&
        s.produtoId != null &&
        s.setorId === t.setorId &&
        s.produtoId === t.produtoId);
    if (exata)
        return exata.peso;
    const porSetor = sobrescritas.find((s) => s.setorId != null && s.produtoId == null && s.setorId === t.setorId);
    if (porSetor)
        return porSetor.peso;
    const porProduto = sobrescritas.find((s) => s.produtoId != null && s.setorId == null && s.produtoId === t.produtoId);
    if (porProduto)
        return porProduto.peso;
    return padrao;
}
/**
 * Peso efetivo de um critério no dia: média dos pesos aplicáveis a cada
 * setor/produto em que o operador trabalhou, ponderada pelo tempo em cada
 * um. Sem sobrescrita (ou sem tempo apontado), vale o peso padrão.
 */
export function pesoEfetivo(criterio, trabalhos, sobrescritas) {
    const aplicaveis = sobrescritas.filter((s) => s.criterioCodigo === criterio.codigo);
    if (aplicaveis.length === 0)
        return criterio.peso;
    let total = 0;
    let soma = 0;
    for (const t of trabalhos) {
        if (t.segundos <= 0)
            continue;
        total += t.segundos;
        soma += t.segundos * pesoPara(criterio.peso, t, aplicaveis);
    }
    return total > 0 ? soma / total : criterio.peso;
}
/**
 * Dias de casa numa data local ("YYYY-MM-DD"). A admissão é uma data de
 * calendário (coluna DATE: meia-noite UTC), por isso lê-se as partes em UTC
 * e nunca no fuso local — senão 01/03 viraria 29/02.
 */
export function diasDeCasaEm(admitidoEm, data) {
    const [ano, mes, dia] = data.split('-').map(Number);
    const admissao = Date.UTC(admitidoEm.getUTCFullYear(), admitidoEm.getUTCMonth(), admitidoEm.getUTCDate());
    return Math.max(0, Math.round((Date.UTC(ano, mes - 1, dia) - admissao) / 86_400_000));
}
/**
 * Sessões com o fim resolvido: uma sessão aberta termina em `agora` ou no
 * fim previsto (fim do turno do dia em que começou), o que vier antes.
 */
export function normalizarSessoes(sessoes, turno, fuso, agora) {
    return sessoes.map((s) => {
        if (s.encerradaEm)
            return s;
        const fim = fimPrevistoDaSessao(s.iniciadaEm, turno, fuso);
        return { iniciadaEm: s.iniciadaEm, encerradaEm: fim < agora ? fim : agora };
    });
}
/** Janela em que a ficha "aconteceu": do primeiro evento ao último (ou a agora, se segue aberta). */
function janelaDaFicha(eventos, agora) {
    const primeiro = eventos[0];
    const ultimo = eventos[eventos.length - 1];
    if (!primeiro || !ultimo)
        return null;
    const encerrada = ultimo.tipo === 'concluir' || ultimo.tipo === 'cancelar';
    return { inicio: primeiro.em, fim: encerrada ? ultimo.em : agora };
}
/** Agrupa as fichas pelos dias que tocam, uma vez só, para não reduzir tudo em todo dia. */
function fichasPorDia(fichas, dias, agora) {
    const porDia = new Map(dias.map((d) => [d.data, []]));
    for (const f of fichas) {
        const janela = janelaDaFicha(f.eventos, agora);
        if (!janela)
            continue;
        for (const d of dias) {
            if (janela.inicio < d.fim && janela.fim >= d.inicio)
                porDia.get(d.data).push(f);
        }
    }
    return porDia;
}
function media(valores) {
    const v = valores.filter((n) => n != null);
    return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
}
export function avaliarPeriodo(entrada) {
    const { agora, fuso, turno } = entrada;
    const sobrescritas = entrada.sobrescritas ?? [];
    const regras = entrada.regrasFalta ?? REGRAS_FALTA_PADRAO;
    const ausenciaPorDia = new Map((entrada.ausencias ?? []).map((a) => [a.data, a]));
    // Ausência que abona o dia; o atestado pode não abonar (regra da empresa).
    const abona = (a) => a != null && !(regras.atestadoContaComoFalta && a.tipo === 'ATESTADO');
    const diasSemJornada = new Set([...ausenciaPorDia.values()].filter((a) => abona(a)).map((a) => a.data));
    const sessoes = normalizarSessoes(entrada.sessoes, turno, fuso, agora);
    const ajuda = {
        ...(entrada.ajudasPrestadas ? { ajudasPrestadas: entrada.ajudasPrestadas } : {}),
        ...(entrada.motivosAjuda ? { motivosAjuda: entrada.motivosAjuda } : {}),
        ...(entrada.toleranciaAjudaSeg != null
            ? { toleranciaAjudaSeg: entrada.toleranciaAjudaSeg }
            : {}),
    };
    const limitar = (i) => ({
        inicio: i.inicio,
        fim: i.fim < agora ? i.fim : agora,
    });
    const fatias = diasNoIntervalo(entrada.intervalo, fuso).filter((d) => d.inicio <= agora);
    const fichasDoDia = fichasPorDia(entrada.fichas, fatias, agora);
    const dias = [];
    for (const dia of fatias) {
        const ausencia = ausenciaPorDia.get(dia.data) ?? null;
        const temJornada = turno != null && jornadaSegundos(turno, dia.diaSemana) > 0;
        const diaDeTrabalho = temJornada && !abona(ausencia);
        const fichas = fichasDoDia.get(dia.data) ?? [];
        const sessoesDoDia = sessoes.filter((s) => s.iniciadaEm < dia.fim && (s.encerradaEm == null || s.encerradaEm > dia.inicio));
        const iniciadasNoDia = sessoesDoDia.filter((s) => s.iniciadaEm >= dia.inicio && s.iniciadaEm < dia.fim);
        const resumo = calcularResumoPeriodo({
            intervalo: dia,
            agora,
            sessoes: sessoesDoDia,
            fichas,
            motivosProdutivos: entrada.motivosProdutivos,
            ...(entrada.atividades ? { atividades: entrada.atividades } : {}),
            ...ajuda,
            jornadaSeg: turno && diaDeTrabalho
                ? jornadaNoIntervalo(turno, limitar(dia), fuso, diasSemJornada)
                : null,
            ...(turno ? { janelasTurno: janelasDoTurno(turno, dia, fuso, diasSemJornada) } : {}),
            ...(entrada.limites ? { limites: entrada.limites } : {}),
        });
        const trabalhou = iniciadasNoDia.length > 0 || resumo.tempoProdutivoSeg > 0;
        const falta = diaDeTrabalho && !trabalhou && dia.fim <= agora;
        let atrasoSeg = null;
        const janelaTurno = diaDeTrabalho && turno ? turnoNoDia(turno, dia.inicio, fuso) : null;
        if (janelaTurno && iniciadasNoDia.length > 0) {
            const primeiraEntrada = Math.min(...iniciadasNoDia.map((s) => s.iniciadaEm.getTime()));
            atrasoSeg = Math.max(0, Math.round((primeiraEntrada - janelaTurno.inicio.getTime()) / 1000));
        }
        const diasDeCasa = entrada.admitidoEm ? diasDeCasaEm(entrada.admitidoEm, dia.data) : null;
        const tolerancia = diasDeCasa == null ? 0 : toleranciaExperiencia(diasDeCasa, entrada.curva);
        const comCurva = (v) => v == null || diasDeCasa == null ? v : aplicarCurvaExperiencia(v, diasDeCasa, entrada.curva);
        const valores = {
            eficiencia: comCurva(resumo.eficiencia),
            produtividade: comCurva(resumo.produtividade),
            pontualidade: atrasoSeg == null ? null : valorPontualidade(atrasoSeg, entrada.pontualidade),
        };
        const brutos = {
            eficiencia: resumo.eficiencia,
            produtividade: resumo.produtividade,
            pontualidade: valores.pontualidade,
        };
        const trabalhos = fichas.map((f) => {
            const parte = reduzirEventosNoIntervalo(f.eventos, dia, agora);
            return {
                setorId: f.setorId,
                produtoId: f.produtoId,
                segundos: parte.tempoPresetSeg + parte.tempoExecucaoSeg,
            };
        });
        const criterios = entrada.criterios.map((c) => {
            const codigo = c.codigo;
            const valor = trabalhou ? (valores[codigo] ?? null) : null;
            return {
                codigo: c.codigo,
                nome: c.nome,
                peso: pesoEfetivo(c, trabalhos, sobrescritas),
                valorBruto: valor == null ? null : (brutos[codigo] ?? null),
                valor: valor ?? 0,
            };
        });
        const avaliaveis = criterios.filter((c) => c.valorBruto != null);
        const nota = trabalhou && avaliaveis.length > 0
            ? calcularNota(avaliaveis)
            : falta && regras.faltaEntraNaNota
                ? NOTA_MINIMA
                : null;
        dias.push({
            data: dia.data,
            diaSemana: dia.diaSemana,
            diaDeTrabalho,
            ausencia,
            trabalhou,
            falta,
            atrasoSeg,
            resumo,
            diasDeCasa,
            tolerancia,
            criterios,
            nota,
        });
    }
    const resumo = calcularResumoPeriodo({
        intervalo: entrada.intervalo,
        agora,
        sessoes,
        fichas: entrada.fichas,
        motivosProdutivos: entrada.motivosProdutivos,
        ...(entrada.atividades ? { atividades: entrada.atividades } : {}),
        ...ajuda,
        jornadaSeg: turno
            ? jornadaNoIntervalo(turno, limitar(entrada.intervalo), fuso, diasSemJornada)
            : null,
        ...(turno
            ? { janelasTurno: janelasDoTurno(turno, entrada.intervalo, fuso, diasSemJornada) }
            : {}),
        ...(entrada.limites ? { limites: entrada.limites } : {}),
    });
    return {
        dias,
        diasTrabalhados: dias.filter((d) => d.trabalhou).length,
        faltas: dias.filter((d) => d.falta).length,
        diasDeAusencia: dias.filter((d) => abona(d.ausencia)).length,
        perdeFaixaPorFalta: regras.faltasParaPerderFaixa != null &&
            dias.filter((d) => d.falta).length >= regras.faltasParaPerderFaixa,
        nota: mediaMensal(dias.map((d) => d.nota)),
        resumo,
        pontualidadeMedia: media(dias.map((d) => d.atrasoSeg == null ? null : valorPontualidade(d.atrasoSeg, entrada.pontualidade))),
        atrasoMedioSeg: media(dias.map((d) => d.atrasoSeg)),
    };
}
/** Faixa do período: a da nota, salvo quando as faltas tiram a faixa (regra da empresa). */
export function faixaDoPeriodo(avaliacao, faixas) {
    return avaliacao.perdeFaixaPorFalta ? null : classificarFaixa(avaliacao.nota, faixas);
}
//# sourceMappingURL=avaliar-periodo.js.map